import { create } from 'zustand';

// Entity types that can be opened in global detail panels
export type GlobalPanelEntityType =
  | 'job'
  | 'company'
  | 'contact'
  | 'customList'
  | 'sequence'
  | 'task'
  | 'bdLead'
  | 'deal';

// Represents a single panel in the stack
export interface StackedPanel {
  type: GlobalPanelEntityType;
  id: string;
  // Stable identifier for React key - persists across navigation within panel
  panelId: string;
}

// Panel without panelId - used when parsing from URL
export type StackedPanelInput = Omit<StackedPanel, 'panelId'> & { panelId?: string };

// Counter for generating unique panel IDs
let panelIdCounter = 0;
function generatePanelId(): string {
  return `panel-${++panelIdCounter}-${Date.now()}`;
}

// URL param names for each entity type
export const ENTITY_URL_PARAMS: Record<GlobalPanelEntityType, string> = {
  job: 'jobId',
  company: 'companyId',
  contact: 'contactId',
  customList: 'customListId',
  sequence: 'sequenceId',
  task: 'taskId',
  bdLead: 'bdLeadId',
  deal: 'dealId',
};

interface PanelStackState {
  // Panel stack - ordered from bottom to top
  stack: StackedPanel[];

  // Push a new panel onto the stack
  pushPanel: (type: GlobalPanelEntityType, id: string) => void;

  // Pop the top panel off the stack
  popPanel: () => void;

  // Close all panels above a given index (inclusive)
  closeToLevel: (index: number) => void;

  // Replace the ID of a panel at a given index (for navigating within a panel)
  navigateWithinPanel: (index: number, newId: string) => void;

  // Clear all panels
  clearAll: () => void;

  // Set stack from URL (for initial hydration)
  setStackFromUrl: (stack: StackedPanelInput[]) => void;

  // Check if any panel is open
  isAnyPanelOpen: () => boolean;

  // Check if a specific panel is open
  isPanelOpen: (type: GlobalPanelEntityType, id: string) => boolean;

  // Get the index of a panel in the stack
  getPanelIndex: (type: GlobalPanelEntityType, id: string) => number;
}

export const usePanelStackStore = create<PanelStackState>((set, get) => ({
  stack: [],

  pushPanel: (type, id) => {
    set((state) => {
      // Check if this exact panel is already in the stack
      const existingIndex = state.stack.findIndex((p) => p.type === type && p.id === id);
      if (existingIndex >= 0) {
        // Panel already exists, just bring it to top by removing panels above it
        return { stack: state.stack.slice(0, existingIndex + 1) };
      }

      // Check if there's already a panel of this type (same type, different ID)
      const sameTypeIndex = state.stack.findIndex((p) => p.type === type);
      if (sameTypeIndex >= 0) {
        // Replace it with the new ID and remove all panels above it
        // Preserve the panelId so the component doesn't remount
        const existingPanel = state.stack[sameTypeIndex];
        const newStack = state.stack.slice(0, sameTypeIndex);
        return { stack: [...newStack, { type, id, panelId: existingPanel.panelId }] };
      }

      // Add new panel to stack with a new stable panelId
      return { stack: [...state.stack, { type, id, panelId: generatePanelId() }] };
    });
  },

  popPanel: () => {
    set((state) => ({
      stack: state.stack.slice(0, -1),
    }));
  },

  closeToLevel: (index) => {
    set((state) => ({
      stack: state.stack.slice(0, index),
    }));
  },

  navigateWithinPanel: (index, newId) => {
    set((state) => {
      if (index < 0 || index >= state.stack.length) return state;
      const newStack = [...state.stack];
      newStack[index] = { ...newStack[index], id: newId };
      return { stack: newStack };
    });
  },

  clearAll: () => {
    set({ stack: [] });
  },

  setStackFromUrl: (stack) => {
    set({
      stack: stack.map((panel) => ({
        ...panel,
        panelId: panel.panelId || generatePanelId(),
      })),
    });
  },

  isAnyPanelOpen: () => get().stack.length > 0,

  isPanelOpen: (type, id) => get().stack.some((p) => p.type === type && p.id === id),

  getPanelIndex: (type, id) => get().stack.findIndex((p) => p.type === type && p.id === id),
}));
